"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.listReceiptCorrectionRequestsResponseSchema = exports.receiptCorrectionRequestSchema = exports.receiptCorrectionPreviewResponseSchema = exports.receiptCorrectionPartySchema = void 0;
const zod_1 = require("zod");
const financial_correction_stage_response_1 = require("./financial-correction-stage.response");
const financial_payment_correction_request_1 = require("../request/financial-payment-correction.request");
const financial_receipt_correction_request_1 = require("../request/financial-receipt-correction.request");
/**
 * Who / which stay a receipt is under — one shape for both the "now" and the
 * "should be" side, and for both kinds, so the screen renders a before → after
 * without branching on which receipt family it is. A field that does not apply
 * to a kind (an OP receipt has no Bill number or IP number) is `null`, never
 * absent.
 *
 * Names and numbers are SNAPSHOTS taken when the request was made, so the
 * approver reads what the clerk saw even if the patient record changes later.
 */
exports.receiptCorrectionPartySchema = zod_1.z.object({
    patientName: zod_1.z.string().nullable(),
    /** The patient's UHID. */
    opNumber: zod_1.z.string().nullable(),
    /** The stay's `IPB…` number — advances only. */
    billNumber: zod_1.z.string().nullable(),
    /** The patient's IP number — advances only. */
    admissionNumber: zod_1.z.string().nullable(),
    wardName: zod_1.z.string().nullable(),
    stayStatus: zod_1.z.string().nullable(),
    /** The consultant — doctor transfers only (the doctor now / the doctor it should move to). */
    doctorName: zod_1.z.string().nullable().optional(),
    /** The doctor's department — doctor transfers only. */
    departmentName: zod_1.z.string().nullable().optional(),
    /** The queue position on the appointment day (a slot number is a queue position, not a time) — doctor transfers only. */
    slotNumber: zod_1.z.number().int().nullable().optional(),
});
/**
 * THE ANSWER TO "COULD THIS BE MOVED?". `blockers` are written for the person at
 * the desk — each one says what stands in the way, in the words the desk uses.
 * `effects` is the plain-language list of what approval will do, shown before the
 * request is sent and again to the approver.
 */
exports.receiptCorrectionPreviewResponseSchema = zod_1.z.object({
    kind: financial_receipt_correction_request_1.receiptCorrectionKindEnum,
    eligible: zod_1.z.boolean(),
    blockers: zod_1.z.array(zod_1.z.string()),
    receiptNumber: zod_1.z.string().nullable(),
    amountPaise: zod_1.z.number().int(),
    current: exports.receiptCorrectionPartySchema,
    /** `null` until the typed number resolves to a real patient / stay. */
    requested: exports.receiptCorrectionPartySchema.nullable(),
    /** IP_ADVANCE_AMOUNT: the amount the receipt would carry. Null for every other kind. */
    requestedAmountPaise: zod_1.z.number().int().nullable().optional(),
    effects: zod_1.z.array(zod_1.z.string()),
});
exports.receiptCorrectionRequestSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    kind: financial_receipt_correction_request_1.receiptCorrectionKindEnum,
    status: financial_payment_correction_request_1.paymentCorrectionStatusEnum,
    receiptNumber: zod_1.z.string().nullable(),
    amountPaise: zod_1.z.number().int(),
    /** IP_ADVANCE_AMOUNT: the amount the receipt should carry (`amountPaise` is what it carried when the request was made). */
    requestedAmountPaise: zod_1.z.number().int().nullable().optional(),
    current: exports.receiptCorrectionPartySchema,
    requested: exports.receiptCorrectionPartySchema,
    reason: zod_1.z.string(),
    requestedBy: zod_1.z.string().uuid().nullable(),
    requestedByName: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string(),
    /** Where the request is in Request -> Verify -> Approve. See `correctionStageEnum`. */
    stage: financial_correction_stage_response_1.correctionStageEnum,
    verifiedBy: zod_1.z.string().uuid().nullable(),
    verifiedByName: zod_1.z.string().nullable(),
    verifiedAt: zod_1.z.string().nullable(),
    verificationNote: zod_1.z.string().nullable(),
    approvedBy: zod_1.z.string().uuid().nullable(),
    approvedByName: zod_1.z.string().nullable(),
    approvedAt: zod_1.z.string().nullable(),
    decisionReason: zod_1.z.string().nullable(),
    /**
     * WHAT APPROVING WILL DO, worked out NOW for a PENDING walk-in name request (what the approver is deciding on: the lab record that
     * would be renamed, lab reports already released, other bills sharing the lab record) — or why it can no longer be applied. Null for
     * every other request, and once decided.
     */
    effects: zod_1.z.array(zod_1.z.string()).nullable().optional(),
    /** What approval actually changed, one plain sentence per row group. `null` until approved. */
    appliedSummary: zod_1.z.array(zod_1.z.string()).nullable(),
});
exports.listReceiptCorrectionRequestsResponseSchema = zod_1.z.object({
    requests: zod_1.z.array(exports.receiptCorrectionRequestSchema),
});
//# sourceMappingURL=financial-receipt-correction.response.js.map